/**
 * Biconomy REST API Functions
 *
 * REST endpoint helpers for Biconomy v1 spot market data.
 *
 * Endpoints/params/shapes mirror the production Biconomy connector
 * (gitlab.1wq.net/mm/111-connman → connector/biconomy/market/rest/service.go) and were
 * verified against the live api.biconomy.com endpoints.
 *
 * @see https://api.biconomy.com
 */

import type {
  BiconomyDepthRequest,
  BiconomyDepthResponse,
  BiconomyExchangeInfoResponse,
  BiconomyKlinesRequest,
  BiconomyKlinesResponse,
  BiconomyTickersResponse,
} from './types';

import { get } from '../../lib/http';
import type { RestRequestOptions } from '../../lib/types';

const BASE_URL = 'https://api.biconomy.com';

/**
 * Default order book depth (levels per side) — mirrors connector DefaultDepthSize.
 */
export const BICONOMY_DEFAULT_DEPTH_SIZE = 50;

/**
 * Default kline count — mirrors connector DefaultKlineSize.
 */
export const BICONOMY_DEFAULT_KLINE_SIZE = 100;

/**
 * Biconomy REST API endpoints.
 * @see connector/biconomy/market/rest/service.go
 */
export const biconomyRestEndpoints = {
  tickers: `${BASE_URL}/api/v1/tickers`,
  depth: `${BASE_URL}/api/v1/depth`,
  klines: `${BASE_URL}/api/v1/kline`,
  exchangeInfo: `${BASE_URL}/api/v1/exchangeInfo`,
} as const;

/**
 * Format a base/quote pair into a Biconomy symbol.
 * Biconomy spot symbols are uppercase with an underscore separator, e.g. "BTC_USDT".
 *
 * @example
 * formatBiconomySymbol('eth', 'usdt') // "ETH_USDT"
 */
export function formatBiconomySymbol(base: string, quote: string): string {
  return `${base}_${quote}`.toUpperCase();
}

/**
 * Parse a Biconomy symbol like "BTC_USDT" into base/quote.
 *
 * @example
 * parseBiconomySymbol('BTC_USDT') // { base: 'BTC', quote: 'USDT' }
 */
export function parseBiconomySymbol(symbol: string): { base: string; quote: string } | null {
  const [base, quote] = symbol.toUpperCase().split('_');
  if (base && quote) {
    return { base, quote };
  }
  return null;
}

/**
 * Fetches 24h ticker statistics for ALL pairs.
 *
 * NOTE: Biconomy has no per-symbol ticker endpoint, so callers must filter the
 * returned `ticker` array by symbol themselves.
 * @see https://api.biconomy.com/api/v1/tickers
 */
export const getBiconomyTickers = async (
  options?: RestRequestOptions,
): Promise<BiconomyTickersResponse> => {
  const response = await get<BiconomyTickersResponse>({
    url: biconomyRestEndpoints.tickers,
    options,
  });

  return response.data;
};

/**
 * Fetches trading pair information (tick size / precision / status).
 * Returns a bare array.
 * @see https://api.biconomy.com/api/v1/exchangeInfo
 */
export const getBiconomyExchangeInfo = async (
  options?: RestRequestOptions,
): Promise<BiconomyExchangeInfoResponse> => {
  const response = await get<BiconomyExchangeInfoResponse>({
    url: biconomyRestEndpoints.exchangeInfo,
    options,
  });

  return response.data;
};

/**
 * Fetches orderbook depth for a symbol.
 * Returns a RAW { asks, bids } object (no code/data wrapper).
 * @see https://api.biconomy.com/api/v1/depth
 */
export const getBiconomyDepth = async (
  params: BiconomyDepthRequest,
  options?: RestRequestOptions,
): Promise<BiconomyDepthResponse> => {
  const response = await get<BiconomyDepthResponse, BiconomyDepthRequest>({
    url: biconomyRestEndpoints.depth,
    params: { symbol: params.symbol, size: params.size ?? BICONOMY_DEFAULT_DEPTH_SIZE },
    options,
  });

  return response.data;
};

/**
 * Fetches candlestick (kline) data for a symbol.
 *
 * Biconomy uses a `type` param (1min/5min/.../week) and a `size` count.
 * Time-range params are NOT supported. REST candles carry MILLISECOND timestamps and
 * standard [ts, open, high, low, close, volume] ordering.
 * @see https://api.biconomy.com/api/v1/kline
 */
export const getBiconomyKlines = async (
  params: BiconomyKlinesRequest,
  options?: RestRequestOptions,
): Promise<BiconomyKlinesResponse> => {
  const response = await get<BiconomyKlinesResponse, BiconomyKlinesRequest>({
    url: biconomyRestEndpoints.klines,
    params: { symbol: params.symbol, type: params.type, size: params.size ?? BICONOMY_DEFAULT_KLINE_SIZE },
    options,
  });

  return response.data;
};
